// Route metadata marking which route owns a "recently opened" identity in the One Retail shell.
// A route declares `meta.recordRecentlyOpenedInShell = { key, title }`, where each is a string or a
// (route, record) => string resolver. See docs/adrs/017-route-metadata-for-recents.md in the shell.

import { watch } from 'vue';

export const RECENT_META_KEY = 'recordRecentlyOpenedInShell';

const resolve = (value, route, record) =>
  typeof value === 'function' ? value(route, record) : value;

// Deepest-first: the most specific record owns the identity.
// `record.meta`, not `route.meta` — vue-router 3 does not merge meta down the chain.
export function getRecentlyVisitedFromRoute(route) {
  const matched = route?.matched ?? [];
  for (let i = matched.length - 1; i >= 0; i -= 1) {
    const record = matched[i];
    const spec = record.meta?.[RECENT_META_KEY];
    if (spec) {
      const key = String(resolve(spec.key, route, record) ?? '').trim();
      const title = String(resolve(spec.title, route, record) ?? '').trim();
      // The shell persists whatever it is given, so a half-resolved entry would stick.
      return key && title ? { key, title } : null;
    }
  }
  return null;
}

// Running the resolvers inside the getter makes their store reads dependencies, so the entry
// re-reports once the entity loads. Serialised so an unchanged entry compares equal and reports once.
export function watchAndReportCurrentRoute(currentRoute, report) {
  const reportable = () => {
    const route = currentRoute();
    const entry = getRecentlyVisitedFromRoute(route);
    return entry ? JSON.stringify({ ...entry, path: route.fullPath }) : null;
  };

  return watch(
    reportable,
    value => {
      if (value) report(JSON.parse(value));
    },
    { immediate: true }
  );
}
